/**
 * Configurator hardware-fields bridge.
 *
 * The Pixlip product configurator resolves the selected hardware variant and
 * dispatches a `productHardware:change` CustomEvent on `document`, whose
 * `detail` is the hardware variant's PDP fields:
 *
 *   { variantId, sku?, weight?: { value, unit }, whatsInTheBox?, videos: [url],
 *     downloads: [{ title, url }], specifications: [{ title, lines: [string] }] }
 *
 * This mirrors those fields onto the bundle product page so the PDP reflects the
 * customer's configuration: SKU, weight (specification table), "what's in the
 * box", downloads, the hardware videos (rendered into the dedicated
 * "Configurator hardware videos" section), and the specification-table rows
 * built from the variant's `custom.technical_data`. The latest value is also cached by
 * the widget on `window.__gsConfiguratorHardware`, so a late load or a
 * re-rendered node re-applies the last fields instead of reverting.
 *
 * Like `configurator-price.js`, this is loaded on every product page and is an
 * inert no-op unless a configurator is present (`#gsconfigurator-container`) —
 * the event simply never fires otherwise. It attaches behavior externally
 * (a `document` listener + a MutationObserver that re-applies after a
 * `<product-rerender>` swap) rather than registering any custom element, since
 * custom-element tag names are single-definition.
 *
 * The theme owns the DOM. This script only writes into hook nodes the theme
 * renders when a configurator is present:
 *   - [data-hardware-sku]        the <variant-sku> element (has data-sku-label)
 *   - [data-variant-weight]      the specification-table weight <span>
 *   - [data-hardware-box]        wrapper; inner [data-variant-box-content]
 *   - [data-hardware-downloads]  wrapper; inner [data-hardware-downloads-list]
 *   - [data-hardware-videos-section] / [data-hardware-videos-grid] /
 *     [data-hardware-video-proto]  the videos section, grid, and cell prototype
 *   - [data-hardware-specs-anchor] / [data-hardware-spec-proto] /
 *     [data-hardware-specs-toggle]  the specification table's hardware rows
 * Any hook that isn't on the page is skipped.
 */
(function () {
  function isConfiguratorPage() {
    return !!document.getElementById("gsconfigurator-container");
  }

  function currentFields() {
    return window.__gsConfiguratorHardware || null;
  }

  // === Loading state (spinner over the fields about to change) ===
  //
  // Mirrors the price bridge's spinner: on `productHardware:pending` we dim each
  // visible hook and overlay a centred spinner; the next `productHardware:change`
  // (or `:error`) clears it. Same look and technique as configurator-price.js.
  var HW_STYLE_ID = "gs-configurator-hardware-style";
  var HW_LOADING_ATTR = "data-gs-hw-loading";
  // A separate attr for the video cells: their loading state is tied to the
  // iframe `load` event (see markVideosLoading), not the generic pending/change
  // cycle, so `hideSpinners` must not clear it.
  var HW_VIDEO_LOADING_ATTR = "data-gs-video-loading";

  function injectSpinnerStyle() {
    if (document.getElementById(HW_STYLE_ID)) return;
    var box = "[" + HW_LOADING_ATTR + "],[" + HW_VIDEO_LOADING_ATTR + "]";
    var child =
      "[" + HW_LOADING_ATTR + "] > *,[" + HW_VIDEO_LOADING_ATTR + "] > *";
    var spinner =
      "[" + HW_LOADING_ATTR + "]::after,[" + HW_VIDEO_LOADING_ATTR + "]::after";
    var style = document.createElement("style");
    style.id = HW_STYLE_ID;
    style.textContent = [
      // The SKU is a full-width block (`.product-info__sku { display: block }`),
      // which would center the spinner mid-row. Shrink it to its text so the
      // spinner sits over the SKU. Scoped to the configurator's hook (and only
      // while shown), so ordinary product SKUs are untouched.
      "[data-hardware-sku]:not([hidden]){display:inline-block;}",
      box + "{position:relative;}",
      child + "{opacity:0.4;}",
      spinner +
        "{content:'';position:absolute;top:50%;left:50%;width:1em;height:1em;margin:-0.5em 0 0 -0.5em;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:gs-hw-spin .6s linear infinite;z-index:3;}",
      // Video cells are dark (black background), so currentColor would be
      // invisible — force the spinner white there.
      "[" +
        HW_VIDEO_LOADING_ATTR +
        "]::after{border-color:#fff;border-right-color:transparent;}",
      "@keyframes gs-hw-spin{to{transform:rotate(360deg);}}",
    ].join("");
    document.head.appendChild(style);
  }

  // The elements a change updates (in the product-info column). The videos live
  // in their own section that's rebuilt per change, so they aren't spun here.
  function spinnerTargets() {
    var targets = [];
    var selectors = [
      "[data-hardware-sku]",
      "[data-variant-weight]",
      "[data-variant-box-content]",
      "[data-hardware-downloads-list]",
    ];
    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (el) targets.push(el);
    }
    return targets;
  }

  // offsetParent is null for display:none / collapsed-accordion / hidden nodes,
  // so we only spin over what's actually on screen and about to change.
  function isVisible(el) {
    return !!(el && el.offsetParent !== null);
  }

  function showSpinners() {
    injectSpinnerStyle();
    var targets = spinnerTargets();
    for (var i = 0; i < targets.length; i++) {
      if (isVisible(targets[i])) targets[i].setAttribute(HW_LOADING_ATTR, "");
    }
  }

  function hideSpinners() {
    var loading = document.querySelectorAll("[" + HW_LOADING_ATTR + "]");
    for (var i = 0; i < loading.length; i++) {
      loading[i].removeAttribute(HW_LOADING_ATTR);
    }
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // Mirrors Liquid's `escape | newline_to_br` used by the theme's own
  // "what's in the box" rendering, so a configurator value looks identical to a
  // server-rendered one.
  function escapeWithBreaks(value) {
    return escapeHtml(value).replace(/\r\n|\r|\n/g, "<br>");
  }

  // === SKU ===

  function applySku(fields) {
    var node = document.querySelector("[data-hardware-sku]");
    if (!node) return;
    if (!fields.sku) {
      node.hidden = true;
      return;
    }
    var label = node.getAttribute("data-sku-label");
    node.textContent = label ? label + " " + fields.sku : fields.sku;
    node.hidden = false;
  }

  // === Weight (specification table) ===

  var WEIGHT_UNIT_ABBREVIATIONS = {
    GRAMS: "g",
    KILOGRAMS: "kg",
    OUNCES: "oz",
    POUNDS: "lb",
  };

  // Trims a float to at most one decimal without trailing zeros ("12.0" -> "12",
  // "12.50" -> "12.5"). Note: this does not apply locale number formatting the
  // way Liquid's `weight_with_unit` filter does (e.g. a comma decimal on a
  // German storefront) — see the handoff notes.
  function formatWeightNumber(value) {
    var rounded = Math.round(value * 10) / 10;
    return String(rounded);
  }

  function applyWeight(fields) {
    var node = document.querySelector("[data-variant-weight]");
    if (!node || !fields.weight) return;
    var unit = WEIGHT_UNIT_ABBREVIATIONS[fields.weight.unit] || "";
    var text = formatWeightNumber(fields.weight.value);
    node.textContent = unit ? text + " " + unit : text;
  }

  // === What's in the box ===

  function applyBox(fields) {
    var wrapper = document.querySelector("[data-hardware-box]");
    if (!wrapper) return;
    var target = wrapper.querySelector("[data-variant-box-content]");
    if (fields.whatsInTheBox) {
      if (target) target.innerHTML = escapeWithBreaks(fields.whatsInTheBox);
      wrapper.hidden = false;
    } else {
      wrapper.hidden = true;
    }
  }

  // === Downloads ===

  function buildDownloadsHtml(downloads) {
    var items = "";
    for (var i = 0; i < downloads.length; i++) {
      var download = downloads[i];
      if (!download || !download.url) continue;
      var title = download.title ? escapeHtml(download.title) : escapeHtml(download.url);
      items +=
        '<li><a href="' +
        escapeHtml(download.url) +
        '" target="_blank" rel="noopener">' +
        title +
        "</a></li>";
    }
    return items ? "<ul>" + items + "</ul>" : "";
  }

  function applyDownloads(fields) {
    var wrapper = document.querySelector("[data-hardware-downloads]");
    if (!wrapper) return;
    var list = wrapper.querySelector("[data-hardware-downloads-list]") || wrapper;
    var downloads = fields.downloads || [];
    var html = buildDownloadsHtml(downloads);
    if (html) {
      list.innerHTML = html;
      wrapper.hidden = false;
    } else {
      list.innerHTML = "";
      wrapper.hidden = true;
    }
  }

  // === Video ===

  // Extracts the 11-char YouTube id from the common URL shapes the merchant's
  // `custom.video_embed` link might use (watch?v=, youtu.be/, /embed/, /shorts/).
  function parseYouTubeId(url) {
    if (!url) return null;
    var patterns = [
      /[?&]v=([A-Za-z0-9_-]{11})/,
      /youtu\.be\/([A-Za-z0-9_-]{11})/,
      /\/embed\/([A-Za-z0-9_-]{11})/,
      /\/shorts\/([A-Za-z0-9_-]{11})/,
    ];
    for (var i = 0; i < patterns.length; i++) {
      var match = url.match(patterns[i]);
      if (match) return match[1];
    }
    return null;
  }

  // Spinner over each video cell until its YouTube embed finishes loading — the
  // visible delay is the iframe fetching its poster. Cleared on the iframe's
  // `load` (or `error`), with a timeout fallback so a cell can never spin
  // forever if neither fires.
  function markVideosLoading(grid) {
    var iframes = grid.querySelectorAll("iframe");
    for (var i = 0; i < iframes.length; i++) {
      var iframe = iframes[i];
      var cell = iframe.closest(".content-over-media");
      if (!cell) continue;
      cell.setAttribute(HW_VIDEO_LOADING_ATTR, "");
      (function (loadingCell) {
        var clear = function () {
          loadingCell.removeAttribute(HW_VIDEO_LOADING_ATTR);
        };
        iframe.addEventListener("load", clear, { once: true });
        iframe.addEventListener("error", clear, { once: true });
        setTimeout(clear, 8000);
      })(cell);
    }
  }

  // Populate the dedicated hardware-videos section: one media cell per video,
  // built by cloning the section's server-rendered prototype (a YouTube embed
  // iframe carrying the parsed video id). The section is revealed only when
  // there's at least one playable video, and hidden otherwise.
  function applyVideos(fields) {
    var section = document.querySelector("[data-hardware-videos-section]");
    var grid = document.querySelector("[data-hardware-videos-grid]");
    var proto = document.querySelector("[data-hardware-video-proto]");
    if (!section || !grid || !proto) return;
    // Hiding the whole Shopify section wrapper (not just our inner node) drops
    // its collapsing margins too, so an empty section leaves no gap.
    var wrapper = section.closest(".shopify-section") || section;
    var urls = fields.videos || [];
    var cells = "";
    for (var i = 0; i < urls.length; i++) {
      var id = parseYouTubeId(urls[i]);
      if (id) cells += proto.innerHTML.replace(/HARDWARE_VIDEO_ID/g, id);
    }
    if (cells) {
      grid.innerHTML = cells;
      wrapper.setAttribute("data-hw-videos-ready", "");
      markVideosLoading(grid);
    } else {
      grid.innerHTML = "";
      wrapper.removeAttribute("data-hw-videos-ready");
    }
  }

  // === Specifications (specification-table rows) ===

  // Remove only the rows this bridge injected. Authored `row` blocks and the
  // weight row have no `data-hardware-spec-row`, so they're left alone.
  // `querySelectorAll` does not descend into `<template>.content`, so the
  // prototype row inside it is never matched here.
  function clearInjectedSpecs() {
    var previous = document.querySelectorAll("[data-hardware-spec-row]");
    for (var i = 0; i < previous.length; i++) {
      var node = previous[i];
      if (node.parentNode) node.parentNode.removeChild(node);
    }
  }

  // Clone the section's prototype row and fill it in. Text goes in via
  // `textContent`, so a specification containing `<` or `&` can't inject markup.
  function buildSpecRow(proto, spec) {
    var row = proto.content.firstElementChild.cloneNode(true);
    var title = row.querySelector("[data-hardware-spec-title]");
    var list = row.querySelector("[data-hardware-spec-lines]");
    if (title) title.textContent = spec.title;
    if (list) {
      list.innerHTML = "";
      for (var i = 0; i < spec.lines.length; i++) {
        var item = document.createElement("li");
        item.textContent = spec.lines[i];
        list.appendChild(item);
      }
    }
    return row;
  }

  // `<feature-chart>` snapshots its row list in connectedCallback and the
  // section sizes the "View all" toggle from the server-rendered row count —
  // neither knows about rows injected afterwards. Recompute both from what is
  // actually in the table now.
  function syncChartRows(anchor) {
    var table = anchor.closest(".feature-chart__table");
    var chart = anchor.closest("feature-chart");
    if (!table || !chart) return;
    var rows = table.querySelectorAll(".feature-chart__table-row");
    var maxRows = parseInt(chart.getAttribute("max-rows"), 10);
    if (isNaN(maxRows)) maxRows = rows.length;
    var expanded = chart.classList.contains("is-expanded");
    for (var i = 0; i < rows.length; i++) {
      rows[i].hidden = !expanded && i >= maxRows;
    }
    // Keep the element's own snapshot in step so its expand/collapse animation
    // covers the injected rows too.
    chart.featureChartRows = Array.prototype.slice.call(table.childNodes);
    var toggle = chart.querySelector("[data-hardware-specs-toggle]");
    if (toggle) toggle.hidden = rows.length <= maxRows;
  }

  // A table whose rows ALL come from the hardware variant is hidden by the
  // section's own CSS until this attribute is set, so it never shows a heading
  // over an empty chart. A table that also has authored rows isn't hidden by
  // that rule, so setting/removing the attribute there is harmless.
  function markSpecsReady(anchor, hasRows) {
    var section = anchor.closest(".shopify-section");
    if (!section) return;
    if (hasRows) {
      section.setAttribute("data-hw-specs-ready", "");
    } else {
      section.removeAttribute("data-hw-specs-ready");
    }
  }

  function applySpecifications(fields) {
    var anchor = document.querySelector("[data-hardware-specs-anchor]");
    var proto = document.querySelector("[data-hardware-spec-proto]");
    if (!anchor || !proto || !anchor.parentNode) return;
    // Rebuild from scratch on every change: a row that the previous variant had
    // and this one hasn't must disappear, never linger.
    clearInjectedSpecs();
    var specs = fields.specifications || [];
    var injected = 0;
    for (var i = 0; i < specs.length; i++) {
      var spec = specs[i];
      if (!spec || !spec.title) continue;
      if (!spec.lines || !spec.lines.length) continue;
      anchor.parentNode.insertBefore(buildSpecRow(proto, spec), anchor);
      injected++;
    }
    syncChartRows(anchor);
    markSpecsReady(anchor, injected > 0);
  }

  function apply(fields) {
    if (!fields || typeof fields !== "object") return;
    applySku(fields);
    applyWeight(fields);
    applyBox(fields);
    applyDownloads(fields);
    applyVideos(fields);
    applySpecifications(fields);
  }

  // A <product-rerender> can replace the SKU / box / downloads block with a
  // fresh, server-rendered node, reverting it to the placeholder. Re-apply the
  // last known fields whenever a hook node reappears. We react only to ADDED
  // nodes that contain a hook, and our own writes never add a hook element, so
  // this can't loop.
  function nodeHasHook(node) {
    if (node.nodeType !== 1) return false;
    var selector =
      "[data-hardware-sku],[data-variant-weight],[data-hardware-box]," +
      "[data-hardware-downloads]";
    return (
      (node.matches && node.matches(selector)) ||
      (node.querySelector && !!node.querySelector(selector))
    );
  }

  // The SKU / box / downloads hooks live inside <product-rerender>, which can
  // swap them for fresh server-rendered nodes on a variant re-render. Re-apply
  // the last fields whenever a hook node reappears there. (The videos section is
  // separate and rebuilt from the event on every change, so it needs no watch.)
  function observeReRenders() {
    if (typeof MutationObserver === "undefined") return;
    var containers = document.querySelectorAll("product-rerender");
    if (!containers.length) return;
    var observer = new MutationObserver(function (mutations) {
      var sawHook = false;
      for (var i = 0; i < mutations.length && !sawHook; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (nodeHasHook(added[j])) {
            sawHook = true;
            break;
          }
        }
      }
      var fields = currentFields();
      if (sawHook && fields) apply(fields);
    });
    for (var k = 0; k < containers.length; k++) {
      observer.observe(containers[k], { childList: true, subtree: true });
    }
  }

  function init() {
    if (!isConfiguratorPage()) return;
    // Inject the bridge styles up front so the SKU is inline-block from the
    // start (no box-width shift when the first spinner appears).
    injectSpinnerStyle();
    document.addEventListener("productHardware:pending", function () {
      showSpinners();
    });
    document.addEventListener("productHardware:change", function (event) {
      apply(event.detail);
      hideSpinners();
    });
    document.addEventListener("productHardware:error", function () {
      hideSpinners();
    });
    // Apply whatever the widget has already emitted (if the script loaded late).
    var fields = currentFields();
    if (fields) apply(fields);
    observeReRenders();
  }

  // `defer` guarantees the DOM is parsed, but guard readyState anyway.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
